import { isSupabaseConfigured, supabase } from "../lib/supabase";

const STORAGE_KEY = "almoune_admin_menus";

export const normalizeMenu = (menu = {}) => {
  if (!menu || typeof menu !== "object") return menu;
  return {
    id: menu.id || `menu-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: menu.name || "",
    price: Number(menu.price ?? 0),
    businessUnit: menu.businessUnit || menu.business_unit || "traiteur",
    components: (Array.isArray(menu.components) ? menu.components : [])
      .map((c) => String(c || "").trim())
      .filter(Boolean),
    sortOrder: menu.sortOrder === undefined || menu.sortOrder === null || menu.sortOrder === ""
      ? 0
      : Number(menu.sortOrder),
  };
};

export const getMenuBusinessUnit = (menu) =>
  (menu && menu.businessUnit) || "traiteur";

export const filterMenusByUnit = (menus = [], unit) =>
  unit ? menus.filter((m) => getMenuBusinessUnit(m) === unit) : menus;

const readCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return (Array.isArray(parsed) ? parsed : []).map(normalizeMenu);
  } catch (e) {
    console.error("Erreur lecture menus", e);
    return [];
  }
};

const writeCache = (menus) => {
  const normalized = menus.map(normalizeMenu);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch (e) {
    console.error("Erreur écriture menus", e);
  }
  return normalized;
};

export const getMenus = () => readCache();

export const mapMenuRowToMenu = (row = {}) => {
  let components = [];
  if (Array.isArray(row.components)) {
    components = row.components;
  } else if (typeof row.components === "string" && row.components) {
    try {
      components = JSON.parse(row.components);
    } catch (e) {
      components = [];
    }
  }
  return normalizeMenu({
    id: row.id,
    name: row.name || "",
    price: Number(row.price || 0),
    businessUnit: row.business_unit || "traiteur",
    components,
    sortOrder: row.sort_order ?? 0,
  });
};

export const toMenuRow = (menu) => {
  const m = normalizeMenu(menu);
  return {
    id: m.id,
    name: m.name || "Menu",
    price: Number(m.price || 0),
    business_unit: getMenuBusinessUnit(m),
    components: m.components,
    sort_order: m.sortOrder || 0,
    updated_at: new Date().toISOString(),
  };
};

export const syncMenusToSupabase = async (menus) => {
  if (!isSupabaseConfigured || !supabase) return false;
  const rows = menus.map(toMenuRow);
  const CHUNK_SIZE = 200;
  for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
    const chunk = rows.slice(i, i + CHUNK_SIZE);
    const { error } = await supabase.from("menus").upsert(chunk, { onConflict: "id" });
    if (error) throw error;
  }
  return true;
};

export const loadMenus = async () => {
  const local = readCache();

  if (!isSupabaseConfigured || !supabase) {
    return local;
  }

  try {
    const { data, error } = await supabase
      .from("menus")
      .select("id, name, price, business_unit, components, sort_order, updated_at")
      .order("sort_order", { ascending: true });

    if (error) throw error;

    if (!data || data.length === 0) {
      if (local.length > 0) {
        await syncMenusToSupabase(local);
      }
      return local;
    }

    const merged = data.map(mapMenuRowToMenu).sort((a, b) => a.sortOrder - b.sortOrder);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    return merged;
  } catch (e) {
    console.error("Erreur chargement menus Supabase", e);
    return local;
  }
};

export const saveMenus = (menus) => {
  const normalized = (Array.isArray(menus) ? menus : []).map(normalizeMenu);
  writeCache(normalized);
  if (isSupabaseConfigured && supabase) {
    syncMenusToSupabase(normalized).catch((e) =>
      console.error("Erreur synchro menus Supabase", e)
    );
  }
  return normalized;
};

export const saveMenu = async (menu) => {
  const normalized = normalizeMenu(menu);
  const all = readCache();
  const idx = all.findIndex((m) => m.id === normalized.id);
  const updated = idx >= 0 ? all.map((m, i) => (i === idx ? normalized : m)) : [...all, normalized];
  saveMenus(updated);
  return normalized;
};

export const deleteMenu = async (id) => {
  const all = readCache();
  const updated = all.filter((m) => m.id !== id);
  saveMenus(updated);
  if (!isSupabaseConfigured || !supabase) return true;
  try {
    const { error } = await supabase.from("menus").delete().eq("id", id);
    if (error) throw error;
  } catch (err) {
    console.error("Erreur suppression menu Supabase", err);
    return false;
  }
  return true;
};